import {joiResolver} from '@hookform/resolvers/joi';
import {AxiosError} from 'axios';
import {useState} from 'react';
import {useForm, UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {GenericErrorResponse} from '../../../api';
import {UseCustomMutationReturn} from '../../../api-refactored/utils/useCustomMutation';
import {
  PostBlacklistedCandidateRequest,
  usePostBlacklistedCandidate,
} from '../../../api-refactored/candidate/usePostBlacklistedCandidate';
import {CandidateBlacklistFormData} from '../../../models';
import schemaAddCandidateBlacklist from '../schema/schemaAddCandidateBlacklist';
import RouteKey from '../../../routes/key';
import {convertToLocalIsoDate} from '../../../utils/DateUtils';

export interface UseAddCandidateBlacklistReturn {
  /**
   * Function to trigger api to add candidate blacklisting
   */
  addCandidateToBlacklist: (formData: CandidateBlacklistFormData) => void;
  /**
   * React hook form object for the add candidate blacklist form
   */
  form: UseFormReturn<CandidateBlacklistFormData>;
  /**
   * Extra handling error message for the add candidate blacklist api
   */
  submitError: string;
  /**
   * React query mutation object
   */
  apiMutation: UseCustomMutationReturn<
      void,
      AxiosError<GenericErrorResponse>,
      PostBlacklistedCandidateRequest,
      unknown
  >;
}

const {candidateBlacklist} = RouteKey.admin.child;
const {admin} = RouteKey.child;

/**
 * A hook to handle add candidate blacklist logic
 *
 */
export const useAddCandidateBlacklist = (): UseAddCandidateBlacklistReturn => {
  const {t} = useTranslation();
  const [submitError, setSubmitError] = useState('');
  const history = useHistory();

  const form = useForm<CandidateBlacklistFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(schemaAddCandidateBlacklist),
  });

  const apiMutation = usePostBlacklistedCandidate({
    onSuccess: (res, reqData) => {
      history.push(`/${admin}/${candidateBlacklist.route}`, {addedCandidateName: reqData.name});
    },
    onError: (err) => addCandidateBlacklistFailCallback(err),
  });

  const addCandidateBlacklistFailCallback = (err: AxiosError<GenericErrorResponse>) => {
    const applicationCode: string = err.response?.data.code!;
    if (err.response?.status === 400 && applicationCode === '5200') {
      setSubmitError(t('admin->>CandidateBlacklist.ExistingBlacklistedCandidate'));
    } else if (err.response?.status === 400 && applicationCode === '5201') {
      setSubmitError(t('admin->>CandidateBlacklist.CandidateAlreadyHaveAccount'));
    } else {
      setSubmitError(err.response?.data.message!);
    }
  };
  const addCandidateToBlacklist = (formData: CandidateBlacklistFormData) => {
    const data: PostBlacklistedCandidateRequest = {
      name: formData.name,
      dateOfBirth: convertToLocalIsoDate(formData.dateOfBirth),
      examType: formData.examType,
      userIdType: formData.IdType === 'I' ? 'NRIC_OR_FIN' : 'PASSPORT',
    };

    if (formData.IdType === 'I') {
      data.uinfin = formData.uinfinOrPassport;
    } else {
      data.passport = formData.uinfinOrPassport;
    }

    apiMutation.mutate(data);
  };

  return {
    form,
    apiMutation,
    submitError,
    addCandidateToBlacklist,
  };
};
